import { type FileSourceStreamIdPair } from '@sharefiledev/request-list-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { RequestListDataProvider } from '../PluginDataProvider/RequestListDataProvider';
import { RequestListNetworkError } from '../RequestListNetworkError';
import {
	ActivationPayload,
	AddActorsPayload,
	BulkUpdateCategoriesPayload,
	CreateRequestListAttachmentPayload,
	CreateRequestListTaskPayload,
	DispatchNotificationPayload,
	DownloadResponse,
	DownloadStructure,
	PagedResponse,
	Paging,
	RequestListAttachmentDto,
	RequestListDto,
	RequestListEntityType,
	RequestListStatus,
	RequestListTaskDto,
	RequestListTaskStatus,
	RequestListUpdateDto,
	RequestListUsersPayload,
	TaskActionPayload,
	UpdateDueDatesPayload,
} from '../types';

interface Configuration {
	origin: string;
	apiPath: string;
}

const configuration: Configuration = {
	origin: window.origin,
	apiPath: '/io/requestlist/v1/',
};

export function configureHttp(configure: (current: Configuration) => void) {
	configure(configuration);
}

async function createRequest(path: string, init: RequestInit = {}) {
	const url = `${configuration.origin}${configuration.apiPath}${path}`;
	init.headers = { ...init.headers, 'X-BFF-CSRF': 'true' };
	return new Request(url, init);
}

export const DefaultRequestListDataProvider: RequestListDataProvider = {
	async getRequestListsByDestinationId(destinationId: string, paging?: Paging) {
		const searchParams = new URLSearchParams();
		searchParams.append('DestinationID', destinationId);
		if (paging?.pageSize) {
			searchParams.append('PageSize', paging.pageSize.toString());
		}
		if (paging?.continuationToken) {
			searchParams.append('ContinuationToken', paging.continuationToken);
		}

		const request = await createRequest(`lists?${searchParams}`, {
			method: 'GET',
		});
		const response = await fetchWithError(
			request,
			'Unable to get request lists by destination'
		);
		const responseText = await response.text();
		const responseJson = JSON.parse(
			responseText,
			dateReviver
		) as PagedResponse<RequestListDto>;
		return {
			...responseJson,
			items: responseJson.items.map(fixRequestList),
		} as PagedResponse<RequestListDto>;
	},
	async getDeletedListsByDestinationId(destinationId: string) {
		const searchParams = new URLSearchParams();
		searchParams.append('DestinationID', destinationId);

		const request = await createRequest(`lists/deleted?${searchParams}`, {
			method: 'GET',
		});
		const response = await fetchWithError(
			request,
			'Unable to get deleted request lists by destination'
		);
		const responseText = await response.text();
		const responseJson = JSON.parse(
			responseText,
			dateReviver
		) as PagedResponse<RequestListDto>;
		return {
			...responseJson,
			items: responseJson.items.map(fixRequestList),
		} as PagedResponse<RequestListDto>;
	},
	async getRequestListsByActorId(
		userId: string,
		includedStates?: RequestListStatus[],
		paging?: Paging
	): Promise<PagedResponse<RequestListDto>> {
		const searchParams = new URLSearchParams();
		searchParams.append('ActorID', userId);
		includedStates?.forEach(status => {
			searchParams.append('IncludeStates', status);
		});
		if (paging?.pageSize) {
			searchParams.append('PageSize', paging.pageSize.toString());
		}
		if (paging?.continuationToken) {
			searchParams.append('ContinuationToken', paging.continuationToken);
		}

		const request = await createRequest(`lists?${searchParams}`, { method: 'GET' });
		const response = await fetchWithError(
			request,
			'Unable to get request lists by actor'
		);
		const responseText = await response.text();
		const responseJson = JSON.parse(
			responseText,
			dateReviver
		) as PagedResponse<RequestListDto>;
		return {
			...responseJson,
			items: responseJson.items.map(fixRequestList),
		} as PagedResponse<RequestListDto>;
	},

	async getRequestList(requestListId: string) {
		const request = await createRequest(`lists/${requestListId}`, {
			method: 'GET',
		});
		const response = await fetchWithError(request, 'Unable to get request list');
		const responseText = await response.text();
		return fixRequestList(JSON.parse(responseText, dateReviver) as RequestListDto);
	},

	async createRequestList(payload: Partial<RequestListDto>) {
		const request = await createRequest('lists', {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetchWithError(request, 'Unable to create request list');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as RequestListDto;
	},

	async putRequestList(payload: RequestListUpdateDto) {
		const { requestListId } = payload;
		const request = await createRequest(`lists/${requestListId}`, {
			method: 'PUT',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		await fetchWithError(request, 'Unable to update request list');
	},

	async addRequestListActors(requestListId: string, payload: RequestListUsersPayload) {
		const request = await createRequest(`lists/${requestListId}/actors`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		await fetchWithError(request, 'Unable to add actors to request list');
	},

	async removeRequestListActor(requestListId: string, actorId: string) {
		const request = await createRequest(`lists/${requestListId}/actors/${actorId}`, {
			method: 'DELETE',
		});

		await fetchWithError(request, 'Unable to remove actor from request list');
	},

	async getRequestListFiles(requestListId: string) {
		const request = await createRequest(`lists/${requestListId}/attachments`, {
			method: 'GET',
		});

		const response = await fetchWithError(request, 'Unable to get files in request list');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as { items: RequestListAttachmentDto[] };
	},

	async deleteRequestList(requestListId: string, payload?: DispatchNotificationPayload) {
		const request = await createRequest(`lists/${requestListId}`, {
			method: 'DELETE',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});

		await fetchWithError(request, 'Unable to delete request list');
	},

	async restoreRequestList(requestListId: string, payload?: DispatchNotificationPayload) {
		const request = await createRequest(`lists/${requestListId}/restore`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		await fetchWithError(request, 'Unable to restore request list');
	},

	async restoreRequestListTasks(requestListId: string, tasksToRestoreIds: string[]) {
		const request = await createRequest(`lists/${requestListId}/tasks/restore`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(tasksToRestoreIds),
		});

		await fetchWithError(request, 'Unable to restore request list tasks');
	},

	async startRequestList(requestListId: string, payload: ActivationPayload) {
		const request = await createRequest(`lists/${requestListId}/activate`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		const response = await fetchWithError(request, 'Unable to start request list');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as RequestListDto;
	},

	async getTasks(
		requestListid: string,
		requestListResourceId: string,
		includedStates?: RequestListTaskStatus | RequestListTaskStatus[],
		includedActorIds?: string | string[],
		includedCategories?: string | string[],
		includedDueDates?: [Date?, Date?],
		paging?: Paging
	) {
		const searchParams = createSearchParams(
			includedStates,
			includedActorIds,
			includedCategories,
			includedDueDates,
			paging
		);
		const querySearchParams = searchParams.toString();
		const request = await createRequest(
			`lists/${requestListid}/tasks${querySearchParams ? '?' : ''}${querySearchParams}`,
			{
				method: 'GET',
			}
		);

		const response = await fetchWithError(request, 'Unable to get tasks in request list');
		const responseText = await response.text();
		const responseJson = JSON.parse(
			responseText,
			dateReviver
		) as PagedResponse<RequestListTaskDto>;
		return {
			...responseJson,
			items: responseJson.items.map(i => fixTask(i, requestListResourceId)),
		} as PagedResponse<RequestListTaskDto>;
	},

	async getTask(requestListid: string, requestListResourceId: string, taskId: string) {
		const request = await createRequest(`lists/${requestListid}/tasks/${taskId}`, {
			method: 'GET',
		});
		const response = await fetchWithError(request, 'Unable to get task in request list');
		const responseText = await response.text();
		const responseJson = JSON.parse(responseText, dateReviver) as RequestListTaskDto;
		return fixTask(responseJson, requestListResourceId);
	},

	async createTask(requestListid: string, payload: CreateRequestListTaskPayload) {
		const request = await createRequest(`lists/${requestListid}/tasks`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetchWithError(request, 'Unable to create task');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as RequestListTaskDto;
	},

	async bulkCreateTasks(
		requestListid: string,
		payload: { items: CreateRequestListTaskPayload[]; sendEmail: boolean }
	) {
		const request = await createRequest(`lists/${requestListid}/tasks/bulk`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});
		await fetchWithError(request, 'Unable to create tasks');
	},

	async putTask(payload: RequestListTaskDto & { sendEmail?: boolean }) {
		const { requestListId, requestListTaskId } = payload;
		const request = await createRequest(
			`lists/${requestListId}/tasks/${requestListTaskId}`,
			{
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		);

		const response = await fetchWithError(request, 'Unable to update task');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as RequestListTaskDto;
	},

	async addRequestListTaskActors(
		requestListId: string,
		requestListTaskId: string,
		payload: AddActorsPayload
	) {
		const request = await createRequest(
			`lists/${requestListId}/tasks/${requestListTaskId}/actors`,
			{
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		);

		await fetchWithError(request, 'Unable to add actors to request list task');
	},

	async deleteRequestListTaskActor(
		task: RequestListTaskDto,
		userId: string,
		sendEmail?: boolean
	) {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}/actors/${userId}${
				sendEmail ? `?sendEmail=${sendEmail}` : ''
			}`,
			{
				method: 'DELETE',
			}
		);

		await fetchWithError(request, 'Unable to remove actor from task');
	},

	async bulkUpdateDueDates(requestListId: string, payload: UpdateDueDatesPayload) {
		const request = await createRequest(`lists/${requestListId}/tasks/duedates`, {
			method: 'PUT',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});
		await fetchWithError(request, 'Unable to bulk update due dates of tasks');
	},

	async bulkUpdateCategories(
		requestListId: string,
		payload: BulkUpdateCategoriesPayload
	) {
		const request = await createRequest(`lists/${requestListId}/tasks/categories`, {
			method: 'PUT',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});
		await fetchWithError(request, 'Unable to bulk update categories of tasks');
	},

	async submitTask(task: RequestListTaskDto, payload: TaskActionPayload) {
		const { requestListId, requestListTaskId } = task;
		const request = await createRequest(
			`lists/${requestListId}/tasks/${requestListTaskId}/sendforreview`,
			{
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		);

		await fetchWithError(request, 'Unable to submit task for review');
	},

	async approveTask(payload: RequestListTaskDto) {
		const { requestListId, requestListTaskId } = payload;
		const request = await createRequest(
			`lists/${requestListId}/tasks/${requestListTaskId}/approve`,
			{
				method: 'POST',
			}
		);

		await fetchWithError(request, 'Unable to approve task');
	},

	async approveTasks(requestList: RequestListDto, tasks: RequestListTaskDto[]) {
		const data = [...tasks.map(t => t.requestListTaskId)];

		const request = await createRequest(
			`lists/${requestList.requestListId}/tasks/approve`,
			{
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(data),
			}
		);

		await fetchWithError(request, 'Unable to approve tasks');
	},

	async rejectTask(task: RequestListTaskDto, payload: TaskActionPayload) {
		const { requestListId, requestListTaskId } = task;
		const request = await createRequest(
			`lists/${requestListId}/tasks/${requestListTaskId}/reject`,
			{
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		);

		await fetchWithError(request, 'Unable to reject task');
	},

	async deleteTask(task: RequestListTaskDto) {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}`,
			{
				method: 'DELETE',
			}
		);

		await fetchWithError(request, 'Unable to delete task');
	},

	async deleteTasks(requestList: RequestListDto, tasks: RequestListTaskDto[]) {
		if (tasks.length === 1) {
			await this.deleteTask(tasks[0]);
			return;
		}

		const request = await createRequest(`lists/${requestList.requestListId}/tasks`, {
			method: 'DELETE',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(tasks.map(t => t.requestListTaskId)),
		});

		await fetchWithError(request, 'Unable to batch delete tasks');
	},

	async getTaskFiles(
		task: RequestListTaskDto
	): Promise<{ items: RequestListAttachmentDto[] }> {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}/attachments`,
			{
				method: 'GET',
			}
		);

		const response = await fetchWithError(request, 'Unable to get files in task');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as { items: RequestListAttachmentDto[] };
	},

	async getTaskAttachments(
		requestListId: string,
		taskId: string
	): Promise<{ items: RequestListAttachmentDto[] }> {
		const request = await createRequest(
			`lists/${requestListId}/tasks/${taskId}/attachments`,
			{
				method: 'GET',
			}
		);

		const response = await fetchWithError(request, 'Unable to get attachments in task');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as { items: RequestListAttachmentDto[] };
	},

	async addTaskAttachment(
		task: RequestListTaskDto,
		payload: CreateRequestListAttachmentPayload
	) {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}/attachments`,
			{
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		);

		const response = await fetchWithError(request, 'Unable to add attachment to task');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as RequestListAttachmentDto;
	},

	async updateTaskAttachment(
		task: RequestListTaskDto,
		attachment: RequestListAttachmentDto,
		attachmentItemId: string
	) {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}/attachments/${attachment.attachmentId}`,
			{
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify({ attachmentItemId }),
			}
		);

		await fetchWithError(request, 'Unable to update attachment in task');
	},

	async deleteTaskAttachment(
		task: Pick<RequestListTaskDto, 'requestListId' | 'requestListTaskId'>,
		attachment: RequestListAttachmentDto
	) {
		const request = await createRequest(
			`lists/${task.requestListId}/tasks/${task.requestListTaskId}/attachments/${attachment.attachmentId}`,
			{
				method: 'DELETE',
			}
		);

		await fetchWithError(request, 'Unable to delete attachment from task');
	},

	async bulkRestoreTaskAttachments(fileSourceStreamIdPairs: FileSourceStreamIdPair[]) {
		const request = await createRequest('attachments/restoreByStreamId', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(fileSourceStreamIdPairs),
		});

		await fetchWithError(request, 'Unable to bulk restore attachments');
	},

	async bulkDeleteTaskAttachments(fileSourceStreamIdPairs: FileSourceStreamIdPair[]) {
		const request = await createRequest('attachments/deleteByStreamId', {
			method: 'DELETE',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(fileSourceStreamIdPairs),
		});

		await fetchWithError(request, 'Unable to bulk delete attachments');
	},

	async completeRequestList(
		requestList: RequestListDto,
		payload: DispatchNotificationPayload
	) {
		const request = await createRequest(`lists/${requestList.requestListId}/complete`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		await fetchWithError(request, 'Unable to complete request list');
	},

	async reopenRequestList(
		requestList: RequestListDto,
		payload: DispatchNotificationPayload
	) {
		const request = await createRequest(`lists/${requestList.requestListId}/reopen`, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		await fetchWithError(request, 'Unable to reopen request list');
	},

	async getDeletedTasks(requestList: RequestListDto) {
		const request = await createRequest(
			`lists/${requestList.requestListId}/tasks/deleted`,
			{
				method: 'GET',
			}
		);
		const response = await fetchWithError(request, 'Unable to fetch deleted tasks');
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as PagedResponse<RequestListTaskDto>;
	},

	async downloadRequestList(
		requestListid: string,
		structure: DownloadStructure,
		includedStates?: RequestListTaskStatus | RequestListTaskStatus[],
		includedActorIds?: string | string[],
		includedCategories?: string | string[],
		includedDueDates?: [Date?, Date?]
	) {
		const searchParams = createSearchParams(
			includedStates,
			includedActorIds,
			includedCategories,
			includedDueDates
		);
		const querySearchParams = searchParams.toString();
		const request = await createRequest(
			`lists/${requestListid}/download/${structure}${
				querySearchParams ? '?' : ''
			}${querySearchParams}`,
			{
				method: 'POST',
			}
		);

		const response = await fetchWithError(
			request,
			`Unable to get download request list with structure ${structure}`
		);
		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as DownloadResponse;
	},
};

async function fetchWithError(request: Request, errorMessage: string) {
	const response = await fetch(request);
	if (response.status > 399) {
		const text = await response.text();
		throw text
			? JSON.parse(text)
			: new RequestListNetworkError(
					`${errorMessage} with status code ${response.status}`,
					response
			  );
	}
	return response;
}

function dateReviver(_key: string, value: string) {
	if (typeof value === 'string') {
		if (
			/^\d{4}-[01]\d-[0-3]\d(T[0-2]\d:[0-5]\d:[0-5]\d(\.\d*)?(Z|[+-]\d{2}:\d{2})?)?$/.test(
				value
			)
		) {
			return new Date(value);
		}
	}
	return value;
}

// todo remove after GA
function fixRequestList(requestList: RequestListDto) {
	if (!requestList.resourceId && requestList.destinationId) {
		requestList.resourceId = RID.parse(requestList.destinationId)
			.createChild(RequestListEntityType.requestlist, requestList.requestListId)
			.toString();
	}
	return requestList;
}

// todo remove after GA
function fixTask(task: RequestListTaskDto, requestListResourceId: string) {
	if (!task.resourceId) {
		task.resourceId = RID.parse(requestListResourceId)
			.createChild(RequestListEntityType.item, task.requestListTaskId)
			.toString();
	}
	return task;
}

function appendQueryParam<T>(name: string, values: T | T[], params: URLSearchParams) {
	if (!values) {
		return;
	}

	if (Array.isArray(values)) {
		values.forEach(value => {
			params.append(name, value as unknown as string);
		});
	} else {
		params.append(name, values as unknown as string);
	}
}

function createSearchParams(
	includedStates?: RequestListTaskStatus | RequestListTaskStatus[],
	includedActorIds?: string | string[],
	includedCategories?: string | string[],
	includedDueDates?: [Date?, Date?],
	paging?: Paging
) {
	const searchParams = new URLSearchParams();

	appendQueryParam('IncludeStates', includedStates, searchParams);
	appendQueryParam('ActorIds', includedActorIds, searchParams);
	appendQueryParam('Categories', includedCategories, searchParams);
	appendQueryParam(
		'DueDateRange.StartDate',
		includedDueDates?.[0]?.toISOString(),
		searchParams
	);
	appendQueryParam(
		'DueDateRange.EndDate',
		includedDueDates?.[1]?.toISOString(),
		searchParams
	);

	if (paging?.pageSize) {
		searchParams.append('PageSize', paging.pageSize.toString());
	}

	if (paging?.continuationToken) {
		searchParams.append('ContinuationToken', paging.continuationToken);
	}

	return searchParams;
}
